<template>
  <!-- Main modal (Bootstrap structure) -->
  <div
    class="modal fade show d-block select-address-modal"
    tabindex="-1"
    role="dialog"
    aria-modal="true"
  >
    <div
      class="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable"
    >
      <div class="modal-content">
        <!-- Header -->
        <div class="modal-header">
          <slot name="header">
            <h1 class="modal-title fs-5">
              {{ showAddressForm ? "주소 추가" : "주소 관리" }}
            </h1>
            <button
              type="button"
              class="btn-close"
              aria-label="Close"
              @click="onClose"
            ></button>
          </slot>
        </div>

        <!-- Body -->
        <div class="modal-body">
          <slot name="body">
            <!-- 주소 목록 화면 -->
            <div v-if="!showAddressForm">
              <div class="d-flex justify-content-end mb-3">
                <button
                  class="btn btn-sm btn-outline-primary"
                  @click="showAddressForm = true"
                >
                  + 새 주소 추가
                </button>
                
              </div>

              <div v-if="addresses.length" class="list-group">
                <div
                  v-for="(address, index) in addresses"
                  :key="index"
                  class="list-group-item list-group-item-action py-3"
                >
                  <div class="d-flex align-items-start justify-content-between">
                    <div class="me-3">
                      <div class="fw-semibold">
                        {{ address.fullName }}
                        <span
                          v-if="address.default"
                          class="badge bg-info-subtle text-info-emphasis ms-2"
                        >
                          기본 주소
                        </span>
                      </div>
                      <div class="text-secondary small">
                        {{ formatPhoneToNational(address.phoneNumber) }}
                      </div>
                    </div>

                    <div class="d-flex gap-2">
                      <button
                        class="btn btn-sm"
                        :class="
                          selectedAddress === index
                            ? 'btn-primary'
                            : 'btn-outline-primary'
                        "
                        @click="selectAddress(index)"
                      >
                        {{ selectedAddress === index ? "Selected" : "선택" }}
                      </button>
                      <button
                        class="btn btn-sm btn-outline-danger"
                        @click="confirmDelete(index)"
                      >
                        삭제
                      </button>
                    </div>
                  </div>

                  <div class="mt-2">
                    <div class="small">
                      {{ address.streetAddress }} {{ address.addressDetail }}
                    </div>
                    <div class="small text-secondary">
                      {{ address.city }}, 대한민국
                    </div>
                  </div>
                </div>
              </div>

              <div v-else class="text-center text-body-secondary py-5">
                등록된 주소가 없습니다.
              </div>
            </div>

            <!-- 신규 배송지 입력 폼 -->
            <form v-else @submit.prevent="saveAddress" novalidate>
              <div class="row g-4">
                <!-- 1️⃣ 이름 / 전화번호 / 이메일 국가(추후 확장시 주석해제) -->
                <div class="col-12 col-md-4">
                  <label class="form-label">이름 <span class="text-danger">*</span></label>
                  <input
                    type="text"
                    v-model="addressData.fullName"
                    placeholder="홍길동"
                    autocomplete="name"
                    :class="['form-control', { 'is-invalid': errors.fullName }]"
                  />
                  <div v-if="errors.fullName" class="invalid-feedback d-block">
                    {{ errors.fullName }}
                  </div>
                </div>

                <div class="col-12 col-md-4">
                  <label class="form-label">전화번호 <span class="text-danger">*</span></label>
                  <div class="input-group">
                    <span class="input-group-text">+82</span>
                    <input
                      id="phoneLocalNumber"
                      type="tel"
                      v-model="addressData.phoneLocalNumber"
                      placeholder="010-1234-5678"
                      autocomplete="tel"
                      :class="['form-control', { 'is-invalid': errors.phoneNumber }]"
                    />
                  </div>
                  <div v-if="errors.phoneNumber" class="invalid-feedback d-block">
                    {{ errors.phoneNumber }}
                  </div>
                </div>
                <div class="col-12 col-md-4">
                  <label class="form-label">이메일</label>
                  <input
                    type="email"
                    v-model="addressData.userEmail"
                    placeholder="example@email.com"
                    autocomplete="email"
                    :class="['form-control', { 'is-invalid': errors.userEmail }]"
                  />
                  <div v-if="errors.userEmail" class="invalid-feedback d-block">
                    {{ errors.userEmail }}
                  </div>
                </div>

                <!-- 2️⃣ 국가 / 전화번호 -->
                <!-- <div class="col-12 col-md-4">
                  <label class="form-label">국가</label>
                  <select
                    v-model="selectedCountry"
                    :class="['form-select', { 'is-invalid': errors.country }]"
                  >
                    <option v-for="c in countries" :key="c.iso2" :value="c">
                      {{ c.countryName }} ({{ c.callingCode }})
                    </option>
                  </select>
                  <div v-if="errors.country" class="invalid-feedback d-block">
                    {{ errors.country }}
                  </div>
                </div> -->

                

                <!-- 3️⃣ 주소 / 우편번호 -->
                <div class="col-12 col-md-8">
                  <label class="form-label">주소 <span class="text-danger">*</span></label>
                  <div class="position-relative">
                    <input
                      id="streetAddress"
                      type="text"
                      placeholder="주소를 검색하시려면 클릭하세요"
                      v-model="streetAddress"
                      readonly
                      autocomplete="off"
                      @click="openPostcode"
                      :class="['form-control', { 'is-invalid': errors.streetAddress }]"
                      style="padding-right: 2.5rem"
                    />
                    <button
                      type="button"
                      class="btn btn-link text-secondary position-absolute"
                      style="top: 50%; right: 0.5rem; transform: translateY(-50%)"
                      @click="openPostcode"
                      aria-label="주소 검색"
                    >
                      <i class="bi bi-search"></i>
                    </button>
                  </div>
                  <div v-if="errors.streetAddress" class="invalid-feedback d-block">
                    {{ errors.streetAddress }}
                  </div>
                </div>

                <div class="col-12 col-md-4">
                  <label class="form-label">우편번호 <span class="text-danger">*</span></label>
                  <input
                    id="zipcode"
                    type="text"
                    v-model="addressData.zipcode"
                    placeholder="12345"
                    @input="digitsOnly($event)"
                    autocomplete="postal-code"
                    :class="['form-control', { 'is-invalid': errors.zipcode }]"
                  />
                  <div v-if="errors.zipcode" class="invalid-feedback d-block">
                    {{ errors.zipcode }}
                  </div>
                </div>

                <!-- 4️⃣ 상세주소 -->
                <div class="col-12">
                  <label class="form-label">상세주소</label>
                  <input
                    type="text"
                    v-model="addressData.addressDetail"
                    placeholder="건물명, 호수 등"
                    autocomplete="address-line2"
                    class="form-control"
                  />
                </div>

                <!-- 5️⃣ 배송/청구 체크박스 -->
                <div class="col-12">
                  <div class="d-flex gap-3">
                    <div class="form-check">
                      <input
                        id="shippingFlag"
                        class="form-check-input"
                        type="checkbox"
                        v-model="addressData.shippingFlag"
                      />
                      <label class="form-check-label" for="shippingFlag">
                        배송지로 사용
                      </label>
                    </div>
                    <div class="form-check">
                      <input
                        id="billingFlag"
                        class="form-check-input"
                        type="checkbox"
                        v-model="addressData.billingFlag"
                      />
                      <label class="form-check-label" for="billingFlag">
                        청구지로 사용
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </slot>
        </div>

        <!-- Footer -->
        <div class="modal-footer">
          <slot name="footer">
            <button
              type="button"
              class="btn btn-outline-secondary"
              @click="onClose"
            >
              취소
            </button>
            <button
              v-if="showAddressForm"
              type="submit"
              class="btn btn-primary"
              @click="saveAddress"
            >
              저장
            </button>
          </slot>
        </div>
      </div>
    </div>
  </div>

  <!-- Backdrop (Vue에서 직접 표시 제어용) -->
  <div class="modal-backdrop fade show"></div>

  <!-- 삭제 확인 모달 (Bootstrap 구조) -->
  <div
    v-if="showDeleteModal"
    class="modal fade show d-block"
    tabindex="-1"
    role="dialog"
    aria-modal="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-sm">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title fs-6">이 주소를 삭제하시겠습니까?</h2>
          <button
            type="button"
            class="btn-close"
            aria-label="Close"
            @click="showDeleteModal = false"
          ></button>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-outline-secondary"
            @click="showDeleteModal = false"
          >
            취소
          </button>
          <button type="button" class="btn btn-danger" @click="deleteAddress">
            삭제
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="showDeleteModal" class="modal-backdrop fade show"></div>

  <!-- 신규: DaumPostcodeModal (레이어 임베드) -->
  <!-- 그대로 사용 가능합니다 -->
  <DaumPostcodeModal
    v-model="isPostcodeOpen"
    title="주소 검색"
    :initial-height="460"
    :auto-close-on-complete="true"
    @complete="onPostcodeComplete"
    @error="onPostcodeError"
  />
</template>

<script setup>
/** (기존 스크립트 유지) — digitsOnly 대상 input에 id 추가됨(zipcode, phoneLocalNumber) */
import { ref, reactive, onMounted, getCurrentInstance } from "vue";
import { defineEmits } from "vue";
import { logError, logWarn, logInfo, logDebug } from "@/utils/logHelper";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { rawCountries } from "@/constants/countryCodeData.js";
import { useModalStore } from "@/store/modalStore";

import addressApi from "@/common/api/addressApi";
import userUtils from "@/common/userUtils";

import DaumPostcodeModal from "@/components/common/DaumPostcodeModal.vue";

const { proxy } = getCurrentInstance();
const streetAddress = ref(""); // 빈 문자열로 초기화
const zipcode = ref("");
const city = ref("");

const emits = defineEmits(["close", "submitAddress"]);
const modalStore = useModalStore();
const userInfo = userUtils.getUserInfo();

const isPostcodeOpen = ref(false);
const addresses = ref([]);
const selectedAddress = ref(
  localStorage.getItem("selectedAddress")
    ? Number(localStorage.getItem("selectedAddress"))
    : null
);

// const countries = rawCountries;
// rawCountries 배열에서 iso2 코드가 'KR'인 객체를 찾아 기본값으로 설정합니다.
//const koreaObject = rawCountries.find((c) => c.iso2 === "KR");
const selectedCountry = ref({
  iso2: "KR",
  countryName: "대한민국",
  callingCode: "+82"
});

const showAddressForm = ref(false);
const showDeleteModal = ref(false);
const addressToDelete = ref(null);

const addressData = reactive({
  fullName: "",
  phoneLocalNumber: "",
  userEmail: "",
  companyName: "",
  streetAddress: "",
  addressDetail: "",
  city: "",
  region: "",
  country: "",
  zipcode: "",
  shippingFlag: false,
  billingFlag: false,
});

const errors = reactive({
  fullName: "",
  userEmail: "",
  streetAddress: "",
  city: "",
  country: "",
  zipcode: "",
  phoneNumber: "",
});

onMounted(() => {
  fetchAddresses();
});

function openPostcode() {
  isPostcodeOpen.value = true;
}

function onPostcodeComplete(d) {
  // 표준 조합: 도로/지번 + (법정동/건물명)
  const base = d.userSelectedType === "R" ? d.roadAddress : d.jibunAddress;
  const ext = [];
  if (d.bname) ext.push(d.bname);
  if (d.buildingName && d.apartment === "Y") ext.push(d.buildingName);
  const display = ext.length ? `${base} (${ext.join(", ")})` : base;

  streetAddress.value = display;
  addressData.streetAddress = display;
  addressData.zipcode = d.zonecode;

  // 시/도: 제공 필드 우선 사용 (없으면 기존 값 유지)
  addressData.city = d.sido || addressData.city || "";

  // KR 자동 설정(필요 시)
  if (!selectedCountry.value || selectedCountry.value.iso2 !== "KR") {
    const kr = rawCountries.find((c) => c.iso2 === "KR");
    if (kr) selectedCountry.value = kr;
  }

  // 에러 초기화
  errors.streetAddress = "";
  errors.zipcode = "";
  errors.city = "";
}

function onPostcodeError(err) {
  logError("[DaumPostcodeModal] 초기화 오류", err);
  getCurrentInstance()?.proxy?.$toast?.error(
    "주소 검색 모듈 초기화에 실패했습니다."
  );
}
async function fetchAddresses() {
  try {
    const response = await addressApi.findUserAddress({
      userSuid: userInfo.userSuid,
    });
    logDebug("주소 목록 응답:", response);
    addresses.value = Array.isArray(response?.addressList)
      ? response.addressList
      : [];
  } catch (error) {
    logError("주소 목록 조회 오류:", error);
    getCurrentInstance()?.proxy?.$toast?.error(
      "주소 목록 조회에 실패했습니다."
    );
  }
}

function selectAddress(index) {
  const selected = addresses.value[index];
  emits("submitAddress", selected);
  emits("close");
}

function confirmDelete(index) {
  addressToDelete.value = index;
  showDeleteModal.value = true;
}

async function deleteAddress() {
  if (addressToDelete.value === null) return;
  const deleteIndex = addressToDelete.value;
  const target = addresses.value[deleteIndex];

  try {
    await addressApi.deleteAddress({
      userSuid: userInfo.userSuid,
      addressId: target.addressId,
    });
    logInfo("주소 삭제 성공:", target.addressId);
    addresses.value.splice(deleteIndex, 1);
    if (selectedAddress.value === deleteIndex) selectedAddress.value = null;
  } catch (error) {
    logError("주소 삭제 오류:", error);
    getCurrentInstance()?.proxy?.$toast?.error(
      "주소 삭제 중 오류가 발생했습니다."
    );
  } finally {
    showDeleteModal.value = false;
  }
}

function digitsOnly(e) {
  e.target.value = e.target.value.replace(/\D/g, "");
  if (e.target.id === "phoneLocalNumber")
    addressData.phoneLocalNumber = e.target.value;
  if (e.target.id === "zipcode") addressData.zipcode = e.target.value;
}

function validateForm() {
  errors.fullName = "";
  errors.userEmail = "";
  errors.streetAddress = "";
  errors.city = "";
  errors.country = "";
  errors.zipcode = "";
  errors.phoneNumber = "";

  let isValid = true;
  if (!addressData.fullName)
    (errors.fullName = "이름은 필수입력입니다."), (isValid = false);
  if (!addressData.streetAddress)
    (errors.streetAddress = "주소는 필수입력입니다."), (isValid = false);
  if (!addressData.city)
    (errors.city = "도시는 필수입력입니다."), (isValid = false);
  if (!addressData.zipcode)
    (errors.zipcode = "우편번호는 필수입력입니다."), (isValid = false);
  if (!addressData.phoneLocalNumber)
    (errors.phoneNumber = "전화번호는 필수입력입니다."), (isValid = false);
  // if (!selectedCountry.value?.iso2)
  //   (errors.country = "국가는 필수선택입니다."), (isValid = false); // 국가 선택은 기본값이 있으므로 주석 처리
  if (!isValid) return isValid;


  try {
    const phoneObj = parsePhoneNumberFromString(
      addressData.phoneLocalNumber,
      selectedCountry.value.iso2
    );
    if (!phoneObj?.isValid())
      (errors.phoneNumber = "유효한 전화번호가 아닙니다."),
        (isValid = false);
  } catch {
    errors.phoneNumber = "전화번호 파싱 중 오류가 발생했습니다.";
    isValid = false;
  }
  return isValid;
}

function formatPhoneToNational(e164Number) {
  if (!e164Number) return "";
  try {
    const phoneObj = parsePhoneNumberFromString(e164Number);
    return phoneObj?.isValid() ? phoneObj.formatNational() : e164Number;
  } catch {
    return e164Number;
  }
}

async function saveAddress() {
  const instance = getCurrentInstance();
  if (!validateForm()) {
    instance?.proxy?.$toast?.error("모든 필수 항목을 입력해 주세요.");
    return;
  }

  let phoneNumberE164 = "";
  try {
    const iso2 = selectedCountry.value?.iso2;
    const phoneObj = parsePhoneNumberFromString(
      addressData.phoneLocalNumber,
      iso2
    );
    if (phoneObj && phoneObj.isValid()) {
      phoneNumberE164 = phoneObj.format("E.164");
    } else {
      throw new Error("유효하지 않은 전화번호입니다.");
    }
  } catch (err) {
    logError("전화번호 변환 오류:", err);
    instance?.proxy?.$toast?.error(
      "전화번호가 잘못되었습니다. 다시 확인해주세요."
    );
    return;
  }

  const payload = {
    userSuid: userInfo.userSuid,
    fullName: addressData.fullName,
    phoneNumber: phoneNumberE164,
    userEmail: addressData.userEmail,
    companyName: addressData.companyName,
    streetAddress: addressData.streetAddress,
    addressDetail: addressData.addressDetail,
    city: addressData.city,
    region: addressData.region,
    country: selectedCountry.value.iso2,
    zipcode: addressData.zipcode,
    shippingFlag: addressData.shippingFlag,
    billingFlag: addressData.billingFlag,
  };

  try {
    await addressApi.createUserAddress(payload);
    logInfo("주소 등록 성공");

    alert("새 주소가 등록되었습니다.");

    setTimeout(async () => {
      await fetchAddresses();
      resetAddressForm();
      showAddressForm.value = false;
    }, 500);
  } catch (error) {
    logError("주소 등록 오류:", error);
    alert("주소 등록에 실패했습니다. 다시 시도해주세요."); 
  }
}

function resetAddressForm() {
  addressData.fullName = "";
  addressData.phoneLocalNumber = "";
  addressData.userEmail = "";
  addressData.companyName = "";
  addressData.streetAddress = "";
  addressData.addressDetail = "";
  addressData.city = "";
  addressData.region = "";
  addressData.country = "";
  addressData.zipcode = "";
  selectedCountry.value = {
  iso2: "KR",
  countryName: "대한민국",
  callingCode: "+82",
};

  errors.fullName = "";
  errors.userEmail = "";
  errors.streetAddress = "";
  errors.city = "";
  errors.country = "";
  errors.zipcode = "";
  errors.phoneNumber = "";
}

function onClose() {
  emits("close");
}
</script>

<style scoped></style>
